import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Star, ArrowUpRight, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value = 0, size = 14, testId }) => (
  <div className="flex items-center gap-0.5" data-testid={testId} aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        className={n <= Math.round(value) ? "text-gold fill-gold" : "text-dim"}
      />
    ))}
  </div>
);

const ReviewCard = ({ review, index }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (review.text || "").length > 240;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`google-review-card-${index}`}
      className="bg-bg border border-line p-8 flex flex-col hover:border-gold/40 transition-colors duration-300"
    >
      <div className="flex items-center justify-between mb-6">
        <Stars value={review.rating} testId={`google-review-stars-${index}`} />
        <span className="text-dim text-[10px] tracking-widerx uppercase">{review.relative_time}</span>
      </div>
      <p className={`text-ink/90 leading-relaxed flex-1 ${!expanded && long ? "line-clamp-5" : ""}`}>
        &ldquo;{review.text}&rdquo;
      </p>
      {long && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 self-start text-gold text-xs tracking-widerx uppercase hover:underline"
          data-testid={`google-review-expand-${index}`}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
      <div className="mt-8 pt-6 border-t border-line flex items-center justify-between gap-3">
        <a
          href={review.author_uri || "#"}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 min-w-0 group"
          data-testid={`google-review-author-${index}`}
        >
          {review.author_photo ? (
            <img src={review.author_photo} alt="" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <span className="w-9 h-9 rounded-full bg-gold/15 text-gold flex items-center justify-center font-display">
              {review.author_name?.[0]}
            </span>
          )}
          <span className="text-ink text-sm truncate group-hover:text-gold transition-colors">{review.author_name}</span>
        </a>
        {review.review_uri && (
          <a
            href={review.review_uri}
            target="_blank"
            rel="noreferrer"
            className="text-muted hover:text-gold transition-colors shrink-0"
            aria-label="View this review on Google"
            data-testid={`google-review-link-${index}`}
          >
            <ArrowUpRight size={16} />
          </a>
        )}
      </div>
    </motion.article>
  );
};

const ReviewsHeader = ({ data }) => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
    <div className="max-w-2xl">
      <div className="eyebrow mb-5">Google Reviews</div>
      <h2 className="font-display text-4xl md:text-6xl text-ink leading-[0.95]">
        Don&apos;t take our word. <span className="text-gold">Take theirs.</span>
      </h2>
    </div>
    <div className="flex items-center gap-6" data-testid="google-reviews-summary">
      <div className="font-display text-7xl text-gold leading-none" data-testid="google-reviews-rating">
        {data.rating?.toFixed(1)}
      </div>
      <div>
        <Stars value={data.rating} size={18} />
        <div className="text-muted text-xs tracking-widerx uppercase mt-2" data-testid="google-reviews-count">
          {data.user_rating_count} reviews on Google
        </div>
      </div>
    </div>
  </div>
);

export const GoogleReviews = ({ className = "bg-elevated" }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/reviews`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, []);

  if (!data?.configured || !data.reviews?.length) return null;

  return (
    <section className={`py-24 lg:py-32 border-y border-line ${className}`} data-testid="google-reviews-section">
      <div className="mx-auto max-w-page px-6 lg:px-10">
        <ReviewsHeader data={data} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.map((r, i) => (
            <ReviewCard key={`${r.author_name}-${i}`} review={r} index={i} />
          ))}
        </div>
        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-dim text-xs">Reviews from Google, shown in Google&apos;s order of relevance.</p>
          <div className="flex flex-wrap gap-4">
            <a href={data.google_maps_uri} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="google-reviews-read-all">
              Read all on Google <ArrowUpRight size={16} />
            </a>
            <a href={data.write_review_uri} target="_blank" rel="noreferrer" className="btn-gold" data-testid="google-reviews-write">
              <PenLine size={16} /> Leave a review
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
